import { useEffect } from 'react';
import {
  LectureData,
  LectureAnswersState,
  LectureQuestion,
  isDecisionTreeQuestion,
  isCaseStudyQuestion,
} from '../types';
import { formatTime, parseTimeStringToSeconds } from '../utils';
import { Answer, DecisionStep } from 'pages/new-questions/types';
import { UserStepAnswer } from 'pages/new-questions/components/DecisionTreeQuestion/utils/types';

interface UseRestoreLectureAnswersProps {
  lecture: LectureData | null;
  setAnswers: React.Dispatch<React.SetStateAction<LectureAnswersState>>;
  setTime: React.Dispatch<React.SetStateAction<number>>;
}

export const useRestoreLectureAnswers = ({
  lecture,
  setAnswers,
  setTime,
}: UseRestoreLectureAnswersProps) => {
  useEffect(() => {
    if (!lecture || !lecture.questions) return;

    const restoredAnswers: LectureAnswersState = {
      objective: {},
      context: {},
      decisionTree: {},
      caseStudy: {},
      questionTime: {},
      contador: '00:00:00',
    };

    let maxTimeSpent = 0;

    lecture.questions.forEach((question: LectureQuestion) => {
      const questionId = question.id_questao;

      // Restaura respostas de Estudo de Caso (step_multiple com answereds)
      if (isCaseStudyQuestion(question)) {
        const stepAnswers: Record<number, number> = {};
        let maxTimeSpentCase = 0;

        question.step_multiple.forEach((step) => {
          const stepAnswered =
            step.answereds && (Array.isArray(step.answereds) ? step.answereds[0] : step.answereds);

          // "Não sei" (answer_id 0) é uma resposta válida — checar presença, não truthiness.
          if (stepAnswered && stepAnswered.answer_id !== undefined && stepAnswered.answer_id !== null) {
            stepAnswers[step.id] = stepAnswered.answer_id;

            if (stepAnswered.time_spent) {
              const seconds = parseTimeStringToSeconds(stepAnswered.time_spent);
              if (seconds > 0) maxTimeSpentCase = Math.max(maxTimeSpentCase, seconds);
            }
          }
        });

        if (Object.keys(stepAnswers).length > 0) {
          // Merge, nunca atribuição: as N entradas do mesmo Estudo de Caso compartilham este
          // balde e cada uma traz só a(s) sua(s) etapa(s) — atribuir descartaria as anteriores
          restoredAnswers.caseStudy[questionId] = {
            ...(restoredAnswers.caseStudy[questionId] ?? {}),
            ...stepAnswers,
          };

          if (maxTimeSpentCase > 0) {
            const previousTime = restoredAnswers.questionTime[questionId];
            const previousSeconds = previousTime ? parseTimeStringToSeconds(previousTime) : 0;
            if (maxTimeSpentCase > previousSeconds) {
              restoredAnswers.questionTime[questionId] = formatTime(maxTimeSpentCase);
            }
            maxTimeSpent = Math.max(maxTimeSpent, maxTimeSpentCase);
          }
        }

        return;
      }

      if (isDecisionTreeQuestion(question)) {
        const userStepAnswers: UserStepAnswer[] = [];
        let maxTimeSpentTree = 0;
        if (question.step_multiple) {
          question.step_multiple.forEach((step: DecisionStep, index: number) => {
            const stepAnswered =
              step.answereds &&
              (Array.isArray(step.answereds) ? step.answereds[0] : step.answereds);

            if (stepAnswered && stepAnswered.answer_id !== undefined && stepAnswered.answer_id !== null) {
              const selectedAnswerId = stepAnswered.answer_id;
              const selectedAnswer = step.answers.find(
                (a: Answer) => a.answer_id === selectedAnswerId
              );

              if (selectedAnswer) {
                userStepAnswers.push({
                  stepIndex: index,
                  stepType: 'multiple',
                  questionText: step.statement,
                  selectedAnswerId: selectedAnswerId,
                  isCorrect: selectedAnswer.correct === 1,
                  allAnswers: step.answers,
                });

                if (stepAnswered.time_spent) {
                  const timeParts = stepAnswered.time_spent.split(':');
                  if (timeParts.length === 3) {
                    const seconds = parseTimeStringToSeconds(stepAnswered.time_spent);
                    if (seconds > 0) maxTimeSpentTree = Math.max(maxTimeSpentTree, seconds);
                  }
                }
              }
            }
          });
        }

        if (question.step_single) {
          question.step_single.forEach((step: DecisionStep, index: number) => {
            const stepAnswered =
              step.answereds &&
              (Array.isArray(step.answereds) ? step.answereds[0] : step.answereds);

            if (stepAnswered && stepAnswered.answer_id !== undefined && stepAnswered.answer_id !== null) {
              const selectedAnswerId = stepAnswered.answer_id;
              const selectedAnswer = step.answers.find(
                (a: Answer) => a.answer_id === selectedAnswerId
              );

              if (selectedAnswer) {
                userStepAnswers.push({
                  stepIndex: index,
                  stepType: 'single',
                  questionText: step.statement,
                  selectedAnswerId: selectedAnswerId,
                  isCorrect: selectedAnswer.correct === 1,
                  allAnswers: step.answers,
                });

                if (stepAnswered.time_spent) {
                  const timeParts = stepAnswered.time_spent.split(':');
                  if (timeParts.length === 3) {
                    const seconds = parseTimeStringToSeconds(stepAnswered.time_spent);
                    if (seconds > 0) maxTimeSpentTree = Math.max(maxTimeSpentTree, seconds);
                  }
                }
              }
            }
          });
        }

        if (userStepAnswers.length > 0) {
          restoredAnswers.decisionTree[questionId] = userStepAnswers;

          if (maxTimeSpentTree > 0) {
            restoredAnswers.questionTime[questionId] = formatTime(maxTimeSpentTree);
            maxTimeSpent = Math.max(maxTimeSpent, maxTimeSpentTree);
          }
        }

        return;
      }

      if (question.answereds && question.answereds.length > 0) {
        const answered = question.answereds[0];

        if (answered.time_spent || answered.elapsed) {
          const timeStr = answered.time_spent || answered.elapsed;
          restoredAnswers.questionTime[questionId] = timeStr;

          const timeParts = timeStr.split(':');
          if (timeParts.length === 3) {
            const seconds = parseTimeStringToSeconds(timeStr);
            if (seconds > 0) maxTimeSpent = Math.max(maxTimeSpent, seconds);
          }
        }

        switch (question.question_type) {
          case 'objective_questions':
            if (answered.answer !== undefined && answered.answer !== null) {
              restoredAnswers.objective[questionId] = answered.answer;
            }
            break;

          case 'question_context':
            if (answered.context_answers) {
              try {
                const contextData = JSON.parse(answered.context_answers);
                restoredAnswers.context[questionId] = contextData.answer || '';
              } catch (error) {
                restoredAnswers.context[questionId] = answered.context_answers;
              }
            }
            break;

          default:
            break;
        }
      }
    });

    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      ...restoredAnswers,
      contador: formatTime(maxTimeSpent),
    }));

    if (maxTimeSpent > 0) {
      setTime(maxTimeSpent);
    }
  }, [lecture, setAnswers, setTime]);
};
